import type { CensusBambooHrResult } from "@/api/census-bamboohr-client";
import { buildCensusDraftWrite, draftToBusiness, draftToMembers } from "@/api/census-draft";
import type { CensusQuickBooksResult } from "@/api/census-quickbooks-client";
import type { StepBuildCeremony } from "@/components/benefits-discovery-v2/step-sequential-build";
import { CakewalkV2Logo } from "@/components/brand/CakewalkV2Logo";
import { useCensusAnalyticsIdentity } from "@/hooks/use-analytics-identity";
import { attributionProps, useCaptureAttributionToken } from "@/hooks/use-attribution-token";
import { useCensusDraft, useSaveCensusDraft, useSubmitCensusDraft } from "@/hooks/use-census-draft";
import type { CensusConnectStep } from "@/hooks/use-census-hr-connect";
import { useCensusSession } from "@/hooks/use-census-session";
import { usePageMeta } from "@/hooks/usePageMeta";
import { cn } from "@/lib/utils";
import { AnalyticsIdentity, track } from "@cakewalk/frontend-api";
import { Lock } from "@cakewalk/ui/icons";
import { V2StepIndicator, V2ThemeRoot } from "@cakewalk/ui/v2";
import { signOut } from "aws-amplify/auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyCensusActivateStep } from "./CompanyCensusActivateStep";
import { CompanyCensusBusinessStep } from "./CompanyCensusBusinessStep";
import { CompanyCensusCompleteStep } from "./CompanyCensusCompleteStep";
import { CompanyCensusCreateAccountStep } from "./CompanyCensusCreateAccountStep";
import { CompanyCensusDependentsStep } from "./CompanyCensusDependentsStep";
import { CompanyCensusForgotPasswordStep } from "./CompanyCensusForgotPasswordStep";
import { CompanyCensusGetStartedStep } from "./CompanyCensusGetStartedStep";
import { CompanyCensusIntroStep } from "./CompanyCensusIntroStep";
import { CompanyCensusLoginStep } from "./CompanyCensusLoginStep";
import { CompanyCensusOwnerRequiredStep } from "./CompanyCensusOwnerRequiredStep";
import { CompanyCensusProspectSignupStep } from "./CompanyCensusProspectSignupStep";
import { CompanyCensusRosterApproachStep } from "./CompanyCensusRosterApproachStep";
import { CompanyCensusTeamStep } from "./CompanyCensusTeamStep";
import {
  type PulledBusiness,
  einMismatchWarning,
  makeTeamMember,
  mergePulledBusiness,
  mergePulledMembers,
} from "./data";
import {
  type BusinessInfo,
  EMPTY_BUSINESS,
  EMPTY_SUBMITTER,
  type FlowMode,
  type HrPullSummary,
  type Sex,
  type SubmitterInfo,
  type TeamMember,
} from "./types";

/**
 * Orchestrator for the company-census flow: login (or create an account) →
 * intro → business → team → dependents → confirmation.
 *
 * A small self-contained step machine (mirrors the platforms-demo POC it was
 * lifted from): in-memory state, URL slug kept in sync for the back button.
 * Differences from the demo: real Cognito login + owner-only account creation,
 * and the census DRAFT in db-service is the single source of truth — we seed it
 * on login, pre-fill from it, autosave each page, and submit at the end.
 */

/** `flow_name` for every census analytics event (see docs/tracking-plan.md Flow 11). */
const CENSUS_FLOW = "company_census";

type FlowStep =
  | "login"
  | "start"
  | "prospect"
  | "signup"
  | "activate"
  | "forgot"
  | "intro"
  | "business"
  | "roster"
  | "team"
  | "dependents"
  | "complete";

/** Where the team roster came from — drives the team step's copy. */
type RosterSource = "quickbooks" | "bamboohr" | "manual" | null;

const SLUG_TO_STEP: Record<string, FlowStep> = {
  login: "login",
  start: "start",
  prospect: "prospect",
  signup: "signup",
  activate: "activate",
  forgot: "forgot",
  intro: "intro",
  business: "business",
  roster: "roster",
  team: "team",
  dependents: "dependents",
  complete: "complete",
};

const STEP_TITLE: Record<FlowStep, string> = {
  login: "Log in",
  start: "Get started",
  prospect: "Create account",
  signup: "Create account",
  activate: "Activate account",
  forgot: "Reset password",
  intro: "Get started",
  business: "Your business",
  roster: "Your team",
  team: "Your team",
  dependents: "Dependents",
  complete: "All set",
};

const INDICATOR_STEPS = [
  { id: "business", label: "Business" },
  { id: "team", label: "Your team" },
  { id: "dependents", label: "Dependents" },
];

const STEP_CEREMONY: Record<FlowStep, StepBuildCeremony> = {
  login: "light",
  start: "light",
  prospect: "light",
  signup: "light",
  activate: "light",
  forgot: "light",
  intro: "full",
  business: "full",
  roster: "full",
  team: "light",
  dependents: "light",
  complete: "light",
};

/**
 * Pre-auth steps (the account-entry funnel) — everything before the data steps.
 * Used by the deep-link guards and the centered auth-card layout switch.
 */
const PRE_AUTH_STEPS = new Set<FlowStep>([
  "login",
  "start",
  "prospect",
  "signup",
  "activate",
  "forgot",
]);

function isPreAuthStep(step: FlowStep): boolean {
  return PRE_AUTH_STEPS.has(step);
}

function useFirstVisitBuild(step: string): boolean {
  const seen = useRef<Set<string>>(new Set());
  const first = !seen.current.has(step);
  if (first) seen.current.add(step);
  return first;
}

export function CompanyCensusExperience() {
  const { stepSlug } = useParams<{ stepSlug?: string }>();
  const navigate = useNavigate();

  // The GTM attribution token, read from `?k=` on first mount and held for the tab. It has to
  // happen HERE rather than at the submit site: `go()` navigates without preserving the query
  // string, so `k` is gone from the URL by the time anything is submitted. No-ops entirely
  // when the parameter is absent, which is every organic visit.
  useCaptureAttributionToken();

  const currentStep: FlowStep = (stepSlug && SLUG_TO_STEP[stepSlug]) || "login";

  usePageMeta({
    title: `${STEP_TITLE[currentStep]} · Get your medical quote`,
    description:
      "Confirm your business, add your team and dependents, and we'll prepare your medical quote.",
    robots: "noindex, nofollow",
  });

  const [mode, setMode] = useState<FlowMode | null>(null);
  const [accountEmail, setAccountEmail] = useState("");
  // Explainer shown at the top of the activation flow when we routed the user
  // there for a reason (e.g. a known owner who tried to log in with no login yet).
  const [activateNotice, setActivateNotice] = useState<string | undefined>(undefined);
  // null = not yet resolved; false = signed in but neither owner nor manager,
  // which gates the flow behind the owner-required explainer.
  const [accountCanManage, setAccountCanManage] = useState<boolean | null>(null);
  // Submitter is retained for the business step's API; with guest removed it is
  // always resolved server-side from the session, so it stays empty here.
  const [submitter, setSubmitter] = useState<SubmitterInfo>(EMPTY_SUBMITTER);
  const [business, setBusiness] = useState<BusinessInfo>(EMPTY_BUSINESS);
  const [members, setMembers] = useState<TeamMember[]>(() => [makeTeamMember()]);
  // Where the team roster came from — set on the roster-approach step; drives
  // the team step's "Review your team" vs "Who needs coverage?" copy.
  const [rosterSource, setRosterSource] = useState<RosterSource>(null);
  // What the last HR pull actually did, for the review step's banner. Held here
  // rather than in the team step because the roster step also needs to know a
  // pull has landed (to offer "continue with the roster you already imported"
  // instead of forcing a re-connect).
  const [hrPullSummary, setHrPullSummary] = useState<HrPullSummary | null>(null);

  // Identify logged-in clients to PostHog.
  const analyticsIdentity = useCensusAnalyticsIdentity(mode, accountEmail);

  // Restore a persisted Cognito session on load so a refresh keeps the user
  // logged in (instead of bouncing back to /login and then erroring on
  // re-login). Runs once after the session probe resolves.
  const session = useCensusSession();
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!session.checked || restoredRef.current) return;
    if (session.isAuthenticated && mode === null) {
      restoredRef.current = true;
      setMode("authenticated");
      setAccountCanManage(session.canManage);
      if (session.email) setAccountEmail(session.email);
      // A restored session is a genuine flow entry too — without this, anyone
      // who refreshes or returns with a live Cognito session never appears at
      // the top of the funnel. `entry` lets analysis separate fresh logins from
      // resumed sessions (which can re-fire on each page load).
      // tracking-plan: Flow 11 (company_census) > census_started (P0)
      track("census_started", {
        flow_name: CENSUS_FLOW,
        auth_mode: "authenticated",
        entry: "resumed_session",
      });
      // A returning, already-signed-in user shouldn't see the pre-auth steps.
      if (isPreAuthStep(currentStep)) {
        navigate("/business", { replace: true });
      }
    }
  }, [
    session.checked,
    session.isAuthenticated,
    session.email,
    session.canManage,
    mode,
    currentStep,
    navigate,
  ]);

  const go = useCallback(
    (next: FlowStep) => {
      navigate(`/${next}`);
    },
    [navigate],
  );

  // Deep-link guard: data steps need a mode. If we land past the pre-auth steps
  // without one (refresh / shared link), send the user back to log in — but wait
  // for the session probe first, and never bounce an authenticated user whose
  // mode is still being restored.
  useEffect(() => {
    if (!session.checked || session.isAuthenticated) return;
    if (mode === null && !isPreAuthStep(currentStep)) {
      navigate("/login", { replace: true });
    }
  }, [session.checked, session.isAuthenticated, mode, currentStep, navigate]);

  // The census DRAFT in db-service is the single source of truth. `ensure`
  // returns the existing draft or seeds one from the client's operational data
  // (business + roster + dependents), so we always pre-fill from the draft.
  const canManage = mode === "authenticated" && accountCanManage === true;
  const draft = useCensusDraft(canManage);
  const seededRef = useRef(false);
  useEffect(() => {
    if (!canManage || seededRef.current) return;
    // Wait until the ensure query has settled (resolved or errored) so we seed
    // once, with whatever the draft returned.
    if (draft.isLoading) return;
    seededRef.current = true;

    if (!draft.data) return;
    const seeded = draftToBusiness(draft.data);
    // Don't clobber anything the user already typed.
    setBusiness((prev) => (prev.name || prev.address ? prev : seeded));

    const team = draftToMembers(draft.data);
    if (team.length > 0) {
      setMembers((prev) => {
        const [first] = prev;
        // Leave it alone if the user already started entering a member.
        if (first && (first.firstName.trim() || first.lastName.trim())) return prev;
        return team;
      });
    }
  }, [canManage, draft.isLoading, draft.data]);

  // Best-effort autosave on each page advance. The final submit is authoritative
  // (it persists the full business + roster), so a failed autosave never loses
  // data — it's re-sent on submit.
  const saveDraft = useSaveCensusDraft();
  const persistDraft = useCallback(() => {
    if (!canManage) return;
    saveDraft.mutate(buildCensusDraftWrite(business, members));
  }, [canManage, business, members, saveDraft]);

  const submitDraft = useSubmitCensusDraft();
  const submitError = submitDraft.isError
    ? submitDraft.error instanceof Error
      ? submitDraft.error.message
      : "Something went wrong submitting your details. Please try again."
    : undefined;

  const handleSubmit = useCallback(() => {
    if (mode === null) return;
    submitDraft.mutate(buildCensusDraftWrite(business, members), {
      onSuccess: () => {
        // tracking-plan: Flow 11 (company_census) > census_submitted (P0)
        track("census_submitted", {
          flow_name: CENSUS_FLOW,
          step_name: "dependents",
          auth_mode: mode,
          member_count: members.length,
          has_dependents: members.some((m) => m.dependents.length > 0),
          // Spread, never `gtm_token: null` — with no token this call is byte-identical to
          // what it sent before the token existed, so no existing query over these events
          // changes shape.
          ...attributionProps(),
        });
        go("complete");
      },
      onError: (err) => {
        // tracking-plan: Flow 11 (company_census) > census_submit_failed (P1)
        track("census_submit_failed", {
          flow_name: CENSUS_FLOW,
          step_name: "dependents",
          error_reason: err instanceof Error ? err.message.slice(0, 80) : "unknown",
          // Also on the failure event: a firm that tried and hit an error is a different
          // outcome from one that never arrived, and without this the two are the same
          // silence in the scorecard.
          ...attributionProps(),
        });
      },
    });
  }, [mode, business, members, submitDraft, go]);

  /**
   * Seed the roster (and business, if empty) from a connected HR system, then
   * land on the "Review your team" step.
   *
   * Shared by QuickBooks and BambooHR: both `/result` endpoints return the same
   * `{ members, business }` census shape, so only the roster-source tag and the
   * analytics event differ. Census fields flow into the draft on the normal save
   * path; the full provider payload was already persisted server-side by the
   * callback.
   */
  const seedFromHrPull = useCallback(
    (
      source: "quickbooks" | "bamboohr",
      result: {
        members: Array<{
          externalId: string | null;
          firstName: string;
          lastName: string;
          sex: Sex | null;
          zip: string | null;
          dateOfBirth: string | null;
        }>;
        business: PulledBusiness | null;
        excludedFormerCount: number;
        fieldWarnings?: string[];
      },
    ) => {
      const pulled = result.members.map((m) =>
        makeTeamMember({
          externalId: m.externalId,
          firstName: m.firstName,
          lastName: m.lastName,
          sex: m.sex ?? "",
          zip: m.zip ?? "",
          dateOfBirth: m.dateOfBirth ?? "",
        }),
      );
      // MERGE, never replace: a second pull must not discard the corrections the
      // employer already typed on the review step. See `mergePulledMembers`.
      if (pulled.length > 0) setMembers((prev) => mergePulledMembers(prev, pulled));

      // The business step precedes this one, so the employer has usually typed
      // the EIN already. A different one in their payroll system is flagged on
      // the review step rather than silently kept or overwritten.
      const einWarning = result.business
        ? einMismatchWarning(
            business.ein,
            result.business.ein,
            source === "quickbooks" ? "QuickBooks" : "BambooHR",
          )
        : null;

      setHrPullSummary({
        importedCount: pulled.length,
        excludedFormerCount: result.excludedFormerCount,
        fieldWarnings: [...(einWarning ? [einWarning] : []), ...(result.fieldWarnings ?? [])],
      });

      // Prefill every business field the employer left blank (EIN included,
      // when QuickBooks has it); never overwrite one they typed.
      const pulledBusiness = result.business;
      if (pulledBusiness) setBusiness((prev) => mergePulledBusiness(prev, pulledBusiness));

      setRosterSource(source);
      go("team");
    },
    [go, business.ein],
  );

  const handleQuickBooksConnected = useCallback(
    (result: CensusQuickBooksResult) => {
      seedFromHrPull("quickbooks", result);
      // tracking-plan: Flow 11 (company_census) > census_qb_data_pulled (P1)
      track("census_qb_data_pulled", {
        flow_name: CENSUS_FLOW,
        step_name: "roster",
        employee_count: result.employeeCount,
        payroll_status: result.payrollStatus ?? "unknown",
        has_company_info: result.hasCompanyInfo,
        has_ein: Boolean(result.business?.ein),
        // Census fields QuickBooks left blank (birth dates, sex, ZIPs). Counts
        // only; the warning text never leaves the browser.
        field_warning_count: result.fieldWarnings.length,
        // How many former employees the filter kept off the roster. On the
        // employer who prompted this fix it was the majority of the pull, so
        // it's the number that tells us whether the filter is landing in the
        // wild — and whether it ever over-reaches.
        excluded_former_count: result.excludedFormerCount,
      });
    },
    [seedFromHrPull],
  );

  const handleBambooHrConnected = useCallback(
    (result: CensusBambooHrResult) => {
      seedFromHrPull("bamboohr", result);
      // tracking-plan: Flow 11 (company_census) > census_bamboohr_data_pulled (P1)
      track("census_bamboohr_data_pulled", {
        flow_name: CENSUS_FLOW,
        step_name: "roster",
        employee_count: result.employeeCount,
        has_company_info: result.hasCompanyInfo,
        // How often BambooHR withheld a field the census needs — the signal for
        // whether the scope/permission setup is landing in real accounts.
        field_warning_count: result.fieldWarnings.length,
        // Former employees kept off the roster — the QuickBooks counterpart
        // carries the same property, so the two providers stay comparable.
        excluded_former_count: result.excludedFormerCount,
      });
    },
    [seedFromHrPull],
  );

  const handleQuickBooksStep = useCallback((step: CensusConnectStep, meta?: { error?: string }) => {
    if (step === "authorizing") {
      // tracking-plan: Flow 11 (company_census) > census_qb_oauth_started (P1)
      track("census_qb_oauth_started", { flow_name: CENSUS_FLOW, step_name: "roster" });
    } else if (step === "error") {
      // tracking-plan: Flow 11 (company_census) > census_qb_data_pull_failed (P1)
      track("census_qb_data_pull_failed", {
        flow_name: CENSUS_FLOW,
        step_name: "roster",
        error_reason: meta?.error ?? "unknown",
      });
    }
  }, []);

  const handleBambooHrStep = useCallback((step: CensusConnectStep, meta?: { error?: string }) => {
    if (step === "authorizing") {
      // tracking-plan: Flow 11 (company_census) > census_bamboohr_oauth_started (P1)
      track("census_bamboohr_oauth_started", { flow_name: CENSUS_FLOW, step_name: "roster" });
    } else if (step === "error") {
      // tracking-plan: Flow 11 (company_census) > census_bamboohr_data_pull_failed (P1)
      track("census_bamboohr_data_pull_failed", {
        flow_name: CENSUS_FLOW,
        step_name: "roster",
        // Bounded like `census_submit_failed` above: this string can originate
        // from BambooHR's own `error_description`, relayed through the OAuth
        // callback's redirect — it is not an enum we control.
        error_reason: meta?.error?.slice(0, 80) ?? "unknown",
      });
    }
  }, []);

  // Track "Back" navigations so drop-off within the flow is visible.
  const goBack = useCallback(
    (from: FlowStep, to: FlowStep) => {
      // tracking-plan: Flow 11 (company_census) > census_step_back (P2)
      track("census_step_back", { flow_name: CENSUS_FLOW, step_name: from });
      // Autosave on the way BACK too, not just forward. Roster edits used to
      // exist only in React state until the employer advanced, so someone who
      // corrected 28 people and then stepped back had nothing persisted to fall
      // back on. Best-effort, exactly like the forward save.
      if (from === "business" || from === "team" || from === "dependents") persistDraft();
      go(to);
    },
    [go, persistDraft],
  );

  // Confirmation screen viewed — a render event (the click that got here already
  // fired census_submitted).
  const completeTrackedRef = useRef(false);
  useEffect(() => {
    if (currentStep !== "complete") {
      completeTrackedRef.current = false;
      return;
    }
    if (completeTrackedRef.current) return;
    completeTrackedRef.current = true;
    // tracking-plan: Flow 11 (company_census) > census_completed_viewed (P2)
    track("census_completed_viewed", { flow_name: CENSUS_FLOW, step_name: "complete" });
  }, [currentStep]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset scroll whenever the step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [currentStep]);

  const playBuild = useFirstVisitBuild(currentStep);
  const ceremony = STEP_CEREMONY[currentStep];

  // Gate: a signed-in employee (neither owner nor manager) can't quote on the
  // company's behalf.
  const showOwnerRequired = mode === "authenticated" && accountCanManage === false;

  // The owner-required gate is a hard funnel exit — track it once so the
  // drop-off is visible (otherwise these users vanish after `census_started`).
  const ownerRequiredTrackedRef = useRef(false);
  useEffect(() => {
    if (!showOwnerRequired || ownerRequiredTrackedRef.current) return;
    ownerRequiredTrackedRef.current = true;
    // tracking-plan: Flow 11 (company_census) > census_owner_required (P1)
    track("census_owner_required", { flow_name: CENSUS_FLOW });
  }, [showOwnerRequired]);

  // Drop the (stale) Cognito session so a different account — the owner — can
  // log in. A full reload gives the session probe a clean slate.
  const handleUseDifferentAccount = useCallback(async () => {
    try {
      await signOut();
    } catch {
      // Even if sign-out fails, send them back to a fresh login.
    }
    window.location.assign("/login");
  }, []);

  const indicatorIndex = useMemo(() => {
    // The roster-approach step is part of the "Your team" phase in the indicator.
    const id = currentStep === "roster" ? "team" : currentStep;
    return INDICATOR_STEPS.findIndex((s) => s.id === id);
  }, [currentStep]);
  const showIndicator = indicatorIndex >= 0 && !showOwnerRequired;

  const handleProgressJump = (_id: string, index: number) => {
    const target = INDICATOR_STEPS[index];
    if (target && index <= indicatorIndex) go(target.id as FlowStep);
  };

  return (
    <V2ThemeRoot
      className={cn("bd-v2-flow min-h-screen cw-v2-dashboard-canvas-bg text-cw-v2-ink")}
      data-track={`screen-company-census-${currentStep}`}
    >
      <AnalyticsIdentity input={analyticsIdentity} />
      <div className="flex min-h-screen flex-col">
        {isPreAuthStep(currentStep) ? (
          <header className="flex w-full items-center justify-center bg-cw-v2-surface px-6 py-5">
            <CakewalkV2Logo size="sm" tone="accent" />
          </header>
        ) : (
          <header className="flex w-full items-center justify-between border-b border-cw-v2-border/60 bg-cw-v2-surface px-6 py-5 sm:px-8">
            <CakewalkV2Logo size="sm" tone="accent" />
            <div className="flex items-center gap-2 text-cw-v2-text-muted">
              <Lock className="h-4 w-4" />
              <span className="text-sm">Encrypted &amp; secure</span>
            </div>
          </header>
        )}

        {showIndicator && (
          <div className="px-4 pt-8 pb-2">
            <div className="mx-auto w-full max-w-5xl">
              <V2StepIndicator
                steps={INDICATOR_STEPS}
                currentStep={indicatorIndex}
                onStepClick={handleProgressJump}
              />
            </div>
          </div>
        )}

        {/*
         * Keyed on the step so the route-level enter animation re-fires on each
         * navigation. The settle plays only on a RETURN visit (`!playBuild`) —
         * on first visit the step owns its `cw-blur-in-up` build, so applying
         * both would double-animate (§4h). Reduced motion is handled in CSS.
         */}
        <div key={currentStep} className={cn("flex-1", !playBuild && "cw-census-step-enter")}>
          {showOwnerRequired && (
            <CompanyCensusOwnerRequiredStep
              email={accountEmail}
              onUseDifferentAccount={handleUseDifferentAccount}
            />
          )}

          {!showOwnerRequired && currentStep === "login" && (
            <CompanyCensusLoginStep
              defaultEmail={accountEmail}
              playBuild={playBuild}
              ceremony={ceremony}
              onLogin={(email, canLoginManage) => {
                setMode("authenticated");
                setAccountEmail(email);
                setAccountCanManage(canLoginManage);
                // tracking-plan: Flow 11 (company_census) > census_started (P0)
                track("census_started", {
                  flow_name: CENSUS_FLOW,
                  auth_mode: "authenticated",
                  entry: "login",
                });
                // Employees get the explainer instead of the form.
                go(canLoginManage ? "intro" : "business");
              }}
              onActivate={(prefill) => {
                if (prefill?.email) setAccountEmail(prefill.email);
                setActivateNotice(prefill?.notice);
                go("activate");
              }}
              onGetStarted={() => {
                setActivateNotice(undefined);
                go("start");
              }}
              onForgotPassword={() => go("forgot")}
            />
          )}

          {!showOwnerRequired && currentStep === "start" && (
            <CompanyCensusGetStartedStep
              defaultEmail={accountEmail}
              playBuild={playBuild}
              ceremony={ceremony}
              onHasLogin={(email) => {
                setAccountEmail(email);
                go("login");
              }}
              onNeedsActivation={(email) => {
                setAccountEmail(email);
                setActivateNotice(undefined);
                go("activate");
              }}
              onNewProspect={(email) => {
                setAccountEmail(email);
                go("prospect");
              }}
              onBackToLogin={() => go("login")}
            />
          )}

          {!showOwnerRequired && currentStep === "prospect" && (
            <CompanyCensusProspectSignupStep
              defaultEmail={accountEmail}
              playBuild={playBuild}
              ceremony={ceremony}
              onCreated={(email, canCreateManage) => {
                setMode("authenticated");
                setAccountEmail(email);
                setAccountCanManage(canCreateManage);
                // tracking-plan: Flow 11 (company_census) > census_started (P0)
                track("census_started", {
                  flow_name: CENSUS_FLOW,
                  auth_mode: "authenticated",
                  entry: "prospect",
                });
                go(canCreateManage ? "intro" : "business");
              }}
              onBack={() => go("login")}
            />
          )}

          {!showOwnerRequired && currentStep === "activate" && (
            <CompanyCensusActivateStep
              defaultEmail={accountEmail}
              notice={activateNotice}
              playBuild={playBuild}
              ceremony={ceremony}
              onActivated={(email, canActivateManage) => {
                setMode("authenticated");
                setAccountEmail(email);
                setAccountCanManage(canActivateManage);
                // tracking-plan: Flow 11 (company_census) > census_started (P0)
                track("census_started", {
                  flow_name: CENSUS_FLOW,
                  auth_mode: "authenticated",
                  entry: "activation",
                });
                go(canActivateManage ? "intro" : "business");
              }}
              onBackToLogin={(email) => {
                if (email) setAccountEmail(email);
                setActivateNotice(undefined);
                go("login");
              }}
            />
          )}

          {!showOwnerRequired && currentStep === "forgot" && (
            <CompanyCensusForgotPasswordStep
              defaultEmail={accountEmail}
              playBuild={playBuild}
              ceremony={ceremony}
              onBackToLogin={(email) => {
                if (email) setAccountEmail(email);
                go("login");
              }}
            />
          )}

          {!showOwnerRequired && currentStep === "signup" && (
            <CompanyCensusCreateAccountStep
              defaultEmail={accountEmail}
              playBuild={playBuild}
              ceremony={ceremony}
              onCreated={(email, canCreateManage) => {
                setMode("authenticated");
                setAccountEmail(email);
                setAccountCanManage(canCreateManage);
                // tracking-plan: Flow 11 (company_census) > census_started (P0)
                track("census_started", {
                  flow_name: CENSUS_FLOW,
                  auth_mode: "authenticated",
                  entry: "signup",
                });
                go(canCreateManage ? "intro" : "business");
              }}
              onBack={() => go("login")}
            />
          )}

          {!showOwnerRequired && currentStep === "intro" && mode && (
            <CompanyCensusIntroStep
              playBuild={playBuild}
              ceremony={ceremony}
              onStart={() => {
                // tracking-plan: Flow 11 (company_census) > census_step_completed (P1)
                track("census_step_completed", {
                  flow_name: CENSUS_FLOW,
                  step_name: "intro",
                  step_number: 0,
                });
                go("business");
              }}
            />
          )}

          {!showOwnerRequired && currentStep === "business" && mode && (
            <CompanyCensusBusinessStep
              mode={mode}
              business={business}
              submitter={submitter}
              playBuild={playBuild}
              ceremony={ceremony}
              onChange={setBusiness}
              onSubmitterChange={setSubmitter}
              onBack={() => goBack("business", "intro")}
              onNext={() => {
                persistDraft();
                // tracking-plan: Flow 11 (company_census) > census_step_completed (P1)
                track("census_step_completed", {
                  flow_name: CENSUS_FLOW,
                  step_name: "business",
                  step_number: 1,
                });
                go("roster");
              }}
            />
          )}

          {!showOwnerRequired && currentStep === "roster" && (
            <CompanyCensusRosterApproachStep
              playBuild={playBuild}
              ceremony={ceremony}
              onBack={() => goBack("roster", "business")}
              // A roster already on screen gets a way forward that isn't
              // "connect again". Without it, an employer who stepped back from
              // the review step could only re-run the import.
              importedRoster={
                rosterSource === "quickbooks" || rosterSource === "bamboohr"
                  ? { source: rosterSource, memberCount: members.length }
                  : null
              }
              onContinueWithImported={() => {
                // tracking-plan: Flow 11 (company_census) > census_roster_resumed (P2)
                track("census_roster_resumed", {
                  flow_name: CENSUS_FLOW,
                  step_name: "roster",
                  member_count: members.length,
                });
                go("team");
              }}
              onConnectedPayroll={handleQuickBooksConnected}
              onConnectedBambooHr={handleBambooHrConnected}
              onConnectClicked={(provider) => {
                if (provider === "bamboohr") {
                  // tracking-plan: Flow 11 (company_census) > census_bamboohr_connect_clicked (P1)
                  track("census_bamboohr_connect_clicked", {
                    flow_name: CENSUS_FLOW,
                    step_name: "roster",
                  });
                  return;
                }
                // tracking-plan: Flow 11 (company_census) > census_qb_connect_clicked (P1)
                track("census_qb_connect_clicked", {
                  flow_name: CENSUS_FLOW,
                  step_name: "roster",
                });
              }}
              onChooseManual={() => {
                setRosterSource("manual");
                // tracking-plan: Flow 11 (company_census) > census_manual_entry_chosen (P1)
                track("census_manual_entry_chosen", {
                  flow_name: CENSUS_FLOW,
                  step_name: "roster",
                });
                go("team");
              }}
              onQuickBooksStep={handleQuickBooksStep}
              onBambooHrStep={handleBambooHrStep}
            />
          )}

          {!showOwnerRequired && currentStep === "team" && (
            <CompanyCensusTeamStep
              members={members}
              rosterSource={rosterSource}
              pullSummary={hrPullSummary}
              playBuild={playBuild}
              ceremony={ceremony}
              onChange={setMembers}
              onBack={() => goBack("team", "roster")}
              onNext={() => {
                persistDraft();
                // tracking-plan: Flow 11 (company_census) > census_step_completed (P1)
                track("census_step_completed", {
                  flow_name: CENSUS_FLOW,
                  step_name: "team",
                  step_number: 2,
                });
                go("dependents");
              }}
            />
          )}

          {!showOwnerRequired && currentStep === "dependents" && (
            <CompanyCensusDependentsStep
              members={members}
              playBuild={playBuild}
              ceremony={ceremony}
              isSubmitting={submitDraft.isPending}
              submitError={submitError}
              onChange={setMembers}
              onBack={() => goBack("dependents", "team")}
              onSubmit={handleSubmit}
            />
          )}

          {!showOwnerRequired && currentStep === "complete" && (
            <CompanyCensusCompleteStep
              business={business}
              members={members}
              playBuild={playBuild}
              ceremony={ceremony}
              onBack={() => goBack("complete", "dependents")}
            />
          )}
        </div>
      </div>
    </V2ThemeRoot>
  );
}
